import {
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Typography,
    useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useId } from 'react';

interface Option {
    name: string;
    label: string;
    color?: string;
    backgroundColor?: string;
}

interface Props {
    label: string;
    selected: string[];
    options: Option[];
    width?: string;
    onSelectOptions(options: string[]): void;
}

export default function MultiCheckSelect({
    label,
    options,
    selected,
    width = '100px',
    onSelectOptions,
}: Props) {
    const uid = useId();
    const theme = useTheme();

    const allOptionNames = options.map((op) => op.name);

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as unknown as string[];
        // Sentinel value for "Select all"
        if (value.includes('__ALL__')) {
            if (selected.length === options.length) {
                onSelectOptions([]);
            } else {
                onSelectOptions(allOptionNames);
            }
            return;
        }
        onSelectOptions(value);
    };

    return (
        <FormControl
            sx={{
                backgroundColor: theme.palette.neutral.N_50,
                height: '40px',
                boxSizing: 'border-box',
                borderRadius: '8px',
                color: theme.palette.neutral.gray,
                width,
            }}
        >
            <InputLabel id={`${uid}-label`}>{label}</InputLabel>
            <Select
                labelId={`${uid}-label`}
                id={uid}
                multiple
                label={label}
                value={selected}
                renderValue={(selectedValues) =>
                    `${label} (${(selectedValues as string[]).length})`
                }
                onChange={handleChange}
            >
                <MenuItem value="__ALL__">
                    <Checkbox
                        checked={selected.length === options.length}
                        indeterminate={selected.length > 0 && selected.length < options.length}
                    />
                    <ListItemText primary={`Select all (${options.length})`} />
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={option.name} value={option.name}>
                        <Checkbox checked={selected.indexOf(option.name) !== -1} />
                        <Typography
                            sx={{
                                width: 'fit-content',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                color: option.color,
                                backgroundColor: option.backgroundColor,
                            }}
                        >
                            {option.label}
                        </Typography>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
